import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { CaregiverListPane } from '@apps/web/components/dashboard/caregiver_list_pane';
import { TrainingModePageTourScope } from '@apps/web/components/page_tour/training_mode_page_tour';
import { CAREGIVERS_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';

export const caregiversSearchSchema = z.object({
    caregiverId: z.string().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

const resolveAgentChatDestination = createAgentChatDestinationResolver();

export async function handleCaregiversBeforeLoad({
    search,
}: {
    readonly search: z.infer<typeof caregiversSearchSchema>;
}): Promise<void> {
    if (
        search.chatId === undefined ||
        search.organizationId === undefined ||
        search.mode === undefined ||
        search.slackWebClaim !== undefined
    ) {
        return;
    }

    const resolution = await resolveAgentChatDestination({
        chatId: search.chatId,
        organizationId: search.organizationId,
        mode: search.mode,
    });
    if (
        resolution.kind === 'stale' ||
        resolution.destination.kind === 'panel'
    ) {
        return;
    }

    const normalizedSearch = normalizeAgentChatSearch(search);
    if (
        normalizedSearch.organizationId === undefined ||
        normalizedSearch.mode === undefined
    ) {
        return;
    }
    throw redirect({
        to: '/agent/runs/$runId',
        params: { runId: resolution.destination.runId },
        search: {
            organizationId: normalizedSearch.organizationId,
            mode: normalizedSearch.mode,
        },
        replace: true,
    });
}

export const Route = createFileRoute('/_app/(main)/caregivers')({
    component: CaregiversPage,
    validateSearch: caregiversSearchSchema,
    beforeLoad: handleCaregiversBeforeLoad,
    head: () => ({
        meta: [
            {
                title: 'Caregivers | Phoebe',
            },
        ],
    }),
});

function CaregiversPage() {
    const {
        caregiverId,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return (
        <TrainingModePageTourScope
            pageId="caregivers"
            organizationId={organizationId}
            ready={organizationId !== null}
            steps={CAREGIVERS_PAGE_TOUR_STEPS}
        >
            <CaregiverListPane selectedCaregiverId={caregiverId} />
        </TrainingModePageTourScope>
    );
}
